import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Loader2, Users, AlertTriangle, FileCheck, FileX, CircleDollarSign, Check, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { getTeams, getPlayers, createPlayer, updatePlayer, deletePlayer, getMatches, getSanctions, createSanction, updateSanction, deleteSanction, getSettings, checkHomonymy } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const SANCTION_LABELS = {
  yellow: 'Ammonizione',
  red: 'Espulsione',
  linesman: 'Guardalinee',
  extra_player: 'Extra Giocatore',
};

export const AdminGiocatoriPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedTeam = searchParams.get('team') || '';
  
  const [teams, setTeams] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(preselectedTeam);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ first_name: '', last_name: '', id_card: '', team_id: '', liability_waiver: false, role: 'player' });
  const [adding, setAdding] = useState(false);
  const [homonymyWarning, setHomonymyWarning] = useState(null);

  const [showAddSanction, setShowAddSanction] = useState(false);
  const [newSanction, setNewSanction] = useState({ player_id: '', match_id: '', team_id: '', sanction_type: 'yellow', fine_amount: 0, notes: '' });
  const [addingSanction, setAddingSanction] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [teamsRes, playersRes, matchesRes, settingsRes] = await Promise.all([
        getTeams(), getPlayers(), getMatches(), getSettings()
      ]);
      setTeams(teamsRes.data);
      setAllPlayers(playersRes.data);
      setMatches(matchesRes.data);
      setSettings(settingsRes.data);

      // Auto-select: use preselected team from URL or first team
      const initialTeam = preselectedTeam || (teamsRes.data.length > 0 ? teamsRes.data[0].id : '');
      if (initialTeam && !selectedTeam) {
        setSelectedTeam(initialTeam);
        const sanctionsRes = await getSanctions(initialTeam);
        setSanctions(sanctionsRes.data);
      } else if (selectedTeam) {
        const sanctionsRes = await getSanctions(selectedTeam);
        setSanctions(sanctionsRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch sanctions when team changes
  const fetchSanctions = useCallback(async (teamId) => {
    if (!teamId) return;
    try {
      const res = await getSanctions(teamId);
      setSanctions(res.data);
    } catch (e) {
      console.error('Error fetching sanctions:', e);
    }
  }, []);

  const handleTeamChange = (teamId) => {
    setSelectedTeam(teamId);
    fetchSanctions(teamId);
  };

  const teamPlayers = useMemo(() =>
    allPlayers.filter(p => p.team_id === selectedTeam && p.role !== 'staff'),
    [allPlayers, selectedTeam]
  );

  const teamStaff = useMemo(() =>
    allPlayers.filter(p => p.team_id === selectedTeam && p.role === 'staff'),
    [allPlayers, selectedTeam]
  );

  const teamMatches = useMemo(() =>
    matches.filter(m => m.home_team_id === selectedTeam || m.away_team_id === selectedTeam),
    [matches, selectedTeam]
  );

  const getTeamById = (id) => teams.find(t => t.id === id);
  const getPlayerById = (id) => allPlayers.find(p => p.id === id);

  const selectedTeamObj = getTeamById(selectedTeam);

  // Fine defaults from settings or sanction type
  const getFineDefault = (type) => {
    if (type === 'yellow') return settings?.yellow_card_fine || 10;
    if (type === 'red') return settings?.red_card_fine || 25;
    if (type === 'linesman') return 15;
    if (type === 'extra_player') return 10;
    return 0;
  };

  const handleOpenAddPlayer = () => {
    setNewPlayer({ first_name: '', last_name: '', id_card: '', team_id: selectedTeam, liability_waiver: false, role: 'player' });
    setHomonymyWarning(null);
    setShowAddPlayer(true);
  };

  const handleOpenAddStaff = () => {
    setNewPlayer({ first_name: '', last_name: '', id_card: '', team_id: selectedTeam, liability_waiver: false, role: 'staff' });
    setHomonymyWarning(null);
    setShowAddPlayer(true);
  };

  const handleAddPlayer = async (skipHomonymyCheck = false) => {
    if (!newPlayer.first_name || !newPlayer.last_name || !newPlayer.id_card) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    // Check homonymy before creating (unless user already confirmed)
    if (!skipHomonymyCheck) {
      try {
        const res = await checkHomonymy(newPlayer.first_name.trim(), newPlayer.last_name.trim());
        if (res.data.has_homonymy) {
          const names = res.data.matches.map(m => `${m.first_name} ${m.last_name} (${m.team_name})`).join(', ');
          setHomonymyWarning(names);
          return; // Stop here, user must confirm
        }
      } catch (e) {
        // If check fails, proceed anyway
      }
    }
    
    setHomonymyWarning(null);
    setAdding(true);
    try {
      await createPlayer(newPlayer);
      toast.success('Giocatore aggiunto');
      setShowAddPlayer(false);
      const [playersRes] = await Promise.all([getPlayers()]);
      setAllPlayers(playersRes.data);
      fetchSanctions(selectedTeam);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta');
    } finally {
      setAdding(false);
    }
  };

  const handleToggleWaiver = async (player) => {
    try {
      await updatePlayer(player.id, { liability_waiver: !player.liability_waiver });
      const res = await getPlayers();
      setAllPlayers(res.data);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      await deletePlayer(playerId);
      toast.success('Giocatore eliminato');
      const res = await getPlayers();
      setAllPlayers(res.data);
      fetchSanctions(selectedTeam);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleOpenAddSanction = () => {
    const defaultType = 'yellow';
    setNewSanction({
      player_id: '',
      match_id: '',
      team_id: selectedTeam,
      sanction_type: defaultType,
      fine_amount: getFineDefault(defaultType),
      notes: ''
    });
    setShowAddSanction(true);
  };

  const handleSanctionTypeChange = (type) => {
    setNewSanction(prev => ({
      ...prev,
      sanction_type: type,
      fine_amount: getFineDefault(type),
      player_id: (type === 'linesman') ? '' : prev.player_id,
      match_id: (type === 'linesman' || type === 'extra_player') ? '' : prev.match_id,
    }));
  };

  const handleAddSanction = async () => {
    if (newSanction.sanction_type === 'yellow' || newSanction.sanction_type === 'red') {
      if (!newSanction.player_id || !newSanction.match_id) {
        toast.error('Seleziona giocatore e partita');
        return;
      }
    }
    if (newSanction.sanction_type === 'linesman' && !selectedTeam) {
      toast.error('Seleziona prima una squadra');
      return;
    }
    setAddingSanction(true);
    try {
      const payload = { ...newSanction, team_id: selectedTeam };
      const res = await createSanction(payload);
      toast.success('Sanzione aggiunta');
      if (res.data?.warning) {
        toast.warning(res.data.warning, { duration: 8000 });
      }
      setShowAddSanction(false);
      fetchSanctions(selectedTeam);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta');
    } finally {
      setAddingSanction(false);
    }
  };

  const handleTogglePaid = async (sanction) => {
    try {
      await updateSanction(sanction.id, { is_paid: !sanction.is_paid });
      fetchSanctions(selectedTeam);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteSanction = async (sanctionId) => {
    try {
      await deleteSanction(sanctionId);
      toast.success('Sanzione eliminata');
      fetchSanctions(selectedTeam);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const waiverCount = teamPlayers.filter(p => p.liability_waiver).length;
  const missingWaiverCount = teamPlayers.length - waiverCount;
  const totalFines = sanctions.reduce((sum, s) => sum + (s.fine_amount || 0), 0);
  const paidFines = sanctions.filter(s => s.is_paid).reduce((sum, s) => sum + (s.fine_amount || 0), 0);
  const unpaidFines = totalFines - paidFines;
  const CAPARRA = 50;
  const caparraExceeded = unpaidFines > CAPARRA;

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  };
  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };
  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-giocatori">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">GESTIONE GIOCATORI</h1>
            <p className="text-slate-400 text-sm">{allPlayers.filter(p => p.role !== 'staff').length} giocatori registrati</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleOpenAddSanction}
              variant="outline"
              size="sm"
              className="border-gold text-gold hover:bg-gold/10"
              disabled={!selectedTeam}
              data-testid="add-sanction-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Sanzione
            </Button>
            <Button
              onClick={handleOpenAddPlayer}
              size="sm"
              className="bg-primary text-slate-950 hover:bg-primary/90"
              disabled={!selectedTeam}
              data-testid="add-player-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Giocatore
            </Button>
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca giocatore per nome o cognome (su tutte le squadre)..."
            className="bg-slate-900 border-slate-800 pl-9 pr-9"
            data-testid="player-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              data-testid="player-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Team Selection - NO "Tutte" */}
        <div className="mb-6">
          <Label className="text-xs text-slate-400 mb-2 block">Seleziona squadra</Label>
          <div className="flex flex-wrap gap-2">
            {teams.map(team => {
              const count = allPlayers.filter(p => p.team_id === team.id && p.role !== 'staff').length;
              return (
                <Button
                  key={team.id}
                  variant={selectedTeam === team.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTeamChange(team.id)}
                  className={cn(
                    "text-xs",
                    selectedTeam === team.id ? 'bg-primary text-slate-950' : 'border-slate-700'
                  )}
                  data-testid={`team-filter-${team.id}`}
                >
                  {team.name} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        {/* Search results cross-team */}
        {searchQuery.trim().length >= 2 && (() => {
          const q = searchQuery.trim().toLowerCase();
          const matches = allPlayers.filter(p =>
            (`${p.first_name} ${p.last_name}`).toLowerCase().includes(q) ||
            (`${p.last_name} ${p.first_name}`).toLowerCase().includes(q) ||
            (p.id_card || '').toLowerCase().includes(q)
          );
          return (
            <Card className="bg-slate-900 border-slate-800 mb-6" data-testid="search-results">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Search className="w-4 h-4 text-primary" /> Risultati ricerca
                  <span className="text-xs text-slate-400 font-normal">({matches.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {matches.length === 0 ? (
                  <p className="text-sm text-slate-500">Nessun giocatore trovato per &quot;{searchQuery}&quot;</p>
                ) : (
                  <div className="space-y-2">
                    {matches.map(p => {
                      const tName = teams.find(t => t.id === p.team_id)?.name || '?';
                      return (
                        <button
                          key={p.id}
                          onClick={() => { handleTeamChange(p.team_id); setSearchQuery(''); }}
                          className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg border border-slate-700 text-left transition-colors"
                          data-testid={`search-result-${p.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm truncate">{p.first_name} {p.last_name}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {tName} · CI: {p.id_card || '—'} · {p.role === 'staff' ? 'Dirigente/Guardalinee' : 'Giocatore'}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded shrink-0 ml-2 whitespace-nowrap">Apri squadra</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {selectedTeam && (
          <>
            {/* Stats Row */}
            <div className="flex flex-wrap gap-4 mb-4 text-xs">
              <span className="flex items-center gap-1 text-green-400">
                <FileCheck className="w-3.5 h-3.5" /> {waiverCount} moduli consegnati
              </span>
              {missingWaiverCount > 0 && (
                <span className="flex items-center gap-1 text-red-400">
                  <FileX className="w-3.5 h-3.5" /> {missingWaiverCount} mancanti
                </span>
              )}
              {totalFines > 0 && (
                <>
                  <span className="flex items-center gap-1 text-gold">
                    <CircleDollarSign className="w-3.5 h-3.5" /> Multe: {'\u20AC'}{totalFines.toFixed(2)}
                  </span>
                  <span className={cn("flex items-center gap-1", unpaidFines > 0 ? "text-red-400" : "text-green-400")}>
                    {unpaidFines > 0 ? `${'\u20AC'}${unpaidFines.toFixed(2)} da pagare` : 'Tutto pagato'}
                  </span>
                </>
              )}
            </div>

            {/* Alert caparra superata */}
            {caparraExceeded && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3" data-testid="caparra-alert">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-red-400 text-sm">Caparra superata!</p>
                  <p className="text-xs text-red-300/70">
                    Le sanzioni non pagate ({'\u20AC'}{unpaidFines.toFixed(2)}) superano la caparra di {'\u20AC'}{CAPARRA.toFixed(2)}.
                    Differenza: {'\u20AC'}{(unpaidFines - CAPARRA).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Players List */}
            <Card className="bg-slate-900 border-slate-800 mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Giocatori - {selectedTeamObj?.name} ({teamPlayers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teamPlayers.length > 0 ? (
                  <div className="space-y-2">
                    {teamPlayers.map((player, idx) => {
                      const playerSanctions = sanctions.filter(s => s.player_id === player.id);
                      const yellows = playerSanctions.filter(s => s.sanction_type === 'yellow').length;
                      const reds = playerSanctions.filter(s => s.sanction_type === 'red').length;
                      const isSuspended = yellows >= 3;

                      return (
                        <div key={player.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-5">{idx + 1}.</span>
                                <span className="font-semibold text-sm">{player.first_name} {player.last_name}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-xs text-slate-500">C.I.: {player.id_card}</p>
                                <span className={cn(
                                  "text-[10px] px-1.5 py-0.5 rounded",
                                  player.liability_waiver ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                                )}>
                                  {player.liability_waiver ? 'Scarico OK' : 'Scarico mancante'}
                                </span>
                                {isSuspended && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold" data-testid={`suspended-badge-${player.id}`}>
                                    SQUALIFICATO ({yellows} amm.)
                                  </span>
                                )}
                                {idx >= 12 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold">
                                    Extra ({'\u20AC'}10)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {yellows > 0 && (
                              <div className="flex items-center gap-0.5">
                                <span className="yellow-card" />
                                {yellows > 1 && <span className="text-xs text-gold">{yellows}</span>}
                              </div>
                            )}
                            {reds > 0 && (
                              <div className="flex items-center gap-0.5">
                                <span className="red-card" />
                                {reds > 1 && <span className="text-xs text-red-500">{reds}</span>}
                              </div>
                            )}
                            <button
                              onClick={() => handleToggleWaiver(player)}
                              className={cn(
                                "shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                                player.liability_waiver
                                  ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                  : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                              )}
                              title={player.liability_waiver ? 'Modulo consegnato' : 'Modulo mancante'}
                              data-testid={`waiver-toggle-${player.id}`}
                            >
                              {player.liability_waiver ? <FileCheck className="w-4 h-4" /> : <FileX className="w-4 h-4" />}
                            </button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Elimina giocatore</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Eliminare {player.first_name} {player.last_name}?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeletePlayer(player.id)} className="bg-red-500 hover:bg-red-600">
                                    Elimina
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    Nessun giocatore. Clicca "+ Nuovo Giocatore" per aggiungerne.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Staff: Dirigenti e Guardalinee */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    Dirigenti e Guardalinee - {selectedTeamObj?.name} ({teamStaff.length})
                  </CardTitle>
                  <Button
                    onClick={handleOpenAddStaff}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-7 text-xs"
                    data-testid="add-staff-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Aggiungi
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {teamStaff.length > 0 ? (
                  <div className="space-y-2">
                    {teamStaff.map((person, idx) => (
                      <div
                        key={person.id}
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/30"
                        data-testid={`staff-row-${person.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 w-5">{idx + 1}.</span>
                            <span className="font-semibold text-sm">{person.first_name} {person.last_name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">Staff</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-slate-500">C.I.: {person.id_card}</p>
                            <span className={cn(
                              "text-[10px] px-1.5 py-0.5 rounded",
                              person.liability_waiver ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                            )}>
                              {person.liability_waiver ? 'Scarico OK' : 'Scarico mancante'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleWaiver(person)}
                            className={cn(
                              "p-1.5 rounded-lg transition-colors",
                              person.liability_waiver ? "bg-green-500/10 text-green-400" : "bg-slate-700 text-slate-400 hover:text-white"
                            )}
                          >
                            {person.liability_waiver ? <FileCheck className="w-4 h-4" /> : <FileX className="w-4 h-4" />}
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 h-8 w-8">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Elimina dirigente</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Eliminare {person.first_name} {person.last_name}?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeletePlayer(person.id)} className="bg-red-500 hover:bg-red-600">
                                  Elimina
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-sm">
                    Nessun dirigente o guardalinee registrato.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sanctions for this team */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gold" />
                  Sanzioni - {selectedTeamObj?.name} ({sanctions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sanctions.length > 0 ? (
                  <div className="space-y-2">
                    {sanctions.map(sanction => {
                      const player = sanction.player_id ? getPlayerById(sanction.player_id) : null;
                      const typeLabel = SANCTION_LABELS[sanction.sanction_type] || sanction.sanction_type;
                      const typeColor = {
                        yellow: 'bg-yellow-500/20 text-yellow-400',
                        red: 'bg-red-500/20 text-red-400',
                        linesman: 'bg-blue-500/20 text-blue-400',
                        extra_player: 'bg-gold/20 text-gold',
                      }[sanction.sanction_type] || 'bg-slate-500/20 text-slate-400';

                      return (
                        <div key={sanction.id} className={cn(
                          "flex items-center justify-between p-3 rounded-lg border",
                          sanction.is_paid ? "bg-slate-800/30 border-slate-700/50" : "bg-slate-800/50 border-slate-700"
                        )}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap", typeColor)}>
                              {typeLabel}
                            </span>
                            <div className="min-w-0">
                              <p className={cn("font-medium text-sm truncate", sanction.is_paid && "text-slate-500")}>
                                {player ? `${player.first_name} ${player.last_name}` : sanction.notes || selectedTeamObj?.name}
                              </p>
                              {sanction.notes && player && (
                                <p className="text-xs text-slate-500 truncate">{sanction.notes}</p>
                              )}
                              {sanction.created_at && (
                                <p className="text-[10px] text-slate-600 mt-0.5">Inserita: {formatDateTime(sanction.created_at)}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cn("font-semibold text-sm", sanction.is_paid ? "text-slate-500 line-through" : "text-gold")}>
                              {'\u20AC'}{(sanction.fine_amount || 0).toFixed(2)}
                            </span>
                            <button
                              onClick={() => handleTogglePaid(sanction)}
                              className={cn(
                                "w-7 h-7 rounded flex items-center justify-center transition-colors text-xs",
                                sanction.is_paid
                                  ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                  : "bg-slate-700/50 text-slate-400 hover:bg-slate-700"
                              )}
                              title={sanction.is_paid ? 'Pagata' : 'Non pagata'}
                              data-testid={`paid-toggle-${sanction.id}`}
                            >
                              {sanction.is_paid ? <Check className="w-4 h-4" /> : <CircleDollarSign className="w-3.5 h-3.5" />}
                            </button>
                            <Button
                              variant="ghost" size="icon"
                              className="text-red-400 hover:text-red-300 h-7 w-7"
                              onClick={() => handleDeleteSanction(sanction.id)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-sm">
                    Nessuna sanzione per questa squadra
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Add Player Dialog */}
        <Dialog open={showAddPlayer} onOpenChange={setShowAddPlayer}>
          <DialogContent className="bg-slate-900 border-slate-800 mx-4 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {newPlayer.role === 'staff' ? 'Nuovo Dirigente/Guardalinee' : 'Nuovo Giocatore'}
                <span className="text-primary text-sm font-normal ml-2">{selectedTeamObj?.name}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Nome</Label>
                  <Input value={newPlayer.first_name} onChange={(e) => setNewPlayer({ ...newPlayer, first_name: e.target.value })} className="bg-slate-950 border-slate-800" data-testid="player-first-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Cognome</Label>
                  <Input value={newPlayer.last_name} onChange={(e) => setNewPlayer({ ...newPlayer, last_name: e.target.value })} className="bg-slate-950 border-slate-800" data-testid="player-last-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Carta d'Identita</Label>
                <Input value={newPlayer.id_card} onChange={(e) => setNewPlayer({ ...newPlayer, id_card: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="es. AB1234567" data-testid="player-id-card" />
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-950 rounded-lg border border-slate-800">
                <Checkbox id="liability-waiver" checked={newPlayer.liability_waiver} onCheckedChange={(checked) => setNewPlayer({ ...newPlayer, liability_waiver: !!checked })} data-testid="player-waiver-checkbox" />
                <Label htmlFor="liability-waiver" className="text-sm cursor-pointer flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-primary" />
                  Modulo Scarico Responsabilita consegnato
                </Label>
              </div>
              {teamPlayers.length >= 12 && newPlayer.role !== 'staff' && (
                <div className="p-3 bg-gold/10 border border-gold/30 rounded-lg text-xs text-gold">
                  Attenzione: questo sara il {teamPlayers.length + 1}° giocatore. Dal 13° in poi viene applicata una sanzione di {'\u20AC'}10 alla squadra.
                </div>
              )}
              {homonymyWarning && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="homonymy-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-400">Possibile omonimia</p>
                      <p className="text-xs text-amber-300/80 mt-1">
                        Esiste gia un giocatore con lo stesso nome: <strong>{homonymyWarning}</strong>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Vuoi procedere comunque?</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAddPlayer(false)}>Annulla</Button>
              {homonymyWarning ? (
                <Button onClick={() => handleAddPlayer(true)} disabled={adding} className="bg-amber-500 text-slate-950 hover:bg-amber-600" data-testid="confirm-homonymy-btn">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi comunque'}
                </Button>
              ) : (
                <Button onClick={() => handleAddPlayer(false)} disabled={adding} className="bg-primary text-slate-950" data-testid="save-player-btn">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Sanction Dialog */}
        <Dialog open={showAddSanction} onOpenChange={setShowAddSanction}>
          <DialogContent className="bg-slate-900 border-slate-800 mx-4 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Aggiungi Sanzione
                <span className="text-primary text-sm font-normal ml-2">{selectedTeamObj?.name}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-sm">Tipo Sanzione</Label>
                <Select value={newSanction.sanction_type} onValueChange={handleSanctionTypeChange}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yellow">Ammonizione ({'\u20AC'}{settings?.yellow_card_fine || 10})</SelectItem>
                    <SelectItem value="red">Espulsione ({'\u20AC'}{settings?.red_card_fine || 25})</SelectItem>
                    <SelectItem value="linesman">Guardalinee ({'\u20AC'}15)</SelectItem>
                    <SelectItem value="extra_player">Extra Giocatore ({'\u20AC'}10)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Player selection for yellow/red/extra_player */}
              {(newSanction.sanction_type === 'yellow' || newSanction.sanction_type === 'red' || newSanction.sanction_type === 'extra_player') && (
                <div className="space-y-2">
                  <Label className="text-sm">Giocatore</Label>
                  <Select value={newSanction.player_id} onValueChange={(v) => setNewSanction({ ...newSanction, player_id: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Seleziona giocatore" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamPlayers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Match selection for yellow/red */}
              {(newSanction.sanction_type === 'yellow' || newSanction.sanction_type === 'red') && (
                <div className="space-y-2">
                  <Label className="text-sm">Partita</Label>
                  <Select value={newSanction.match_id} onValueChange={(v) => setNewSanction({ ...newSanction, match_id: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Seleziona partita" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMatches.map(match => {
                        const home = getTeamById(match.home_team_id);
                        const away = getTeamById(match.away_team_id);
                        return (
                          <SelectItem key={match.id} value={match.id}>
                            {formatDate(match.match_date)} {formatTime(match.match_date)} - {home?.name} vs {away?.name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Multa ({'\u20AC'})</Label>
                  <Input type="number" min="0" step="0.01" value={newSanction.fine_amount} onChange={(e) => setNewSanction({ ...newSanction, fine_amount: parseFloat(e.target.value) || 0 })} className="bg-slate-950 border-slate-800" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Note (opzionale)</Label>
                  <Input value={newSanction.notes} onChange={(e) => setNewSanction({ ...newSanction, notes: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="Note" />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAddSanction(false)}>Annulla</Button>
              <Button onClick={handleAddSanction} disabled={addingSanction} className="bg-gold text-slate-950">
                {addingSanction ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminGiocatoriPage;
